import { useRef, useState, useCallback, useEffect } from 'react';

import API from './API';

/**
 *
 * @param apiCall async function(...[*])
 * @param onSuccess function(result)
 * @param onError function(error)
 * @param startAsLoading bool Sets loading return value to initial true, used when the apiCall is suppose to be run directly
 * @returns {[function(...[*]): void, boolean, function(): void]}
 * @example
 *
 * // Get some asset using the async function api.getAssets
 *
 * const [assetValue, setAssetValue] = useState(null);
 *
 * const [getAsset, isLoadingAssets, cancelLoading] =
 *    useApiCall({
 *      apiCall: api.getAssets,
 *      onSuccess: useCallback(result => setAssetValue(result), []),
 *      onError: useCallback(error => ...handle error..., [])
 *    });
 *
 * useEffect(() => {
 *   getAsset(assetId);
 * }, [assetId]);
 *
 * // Note: No need to add cancel loading in the effect. This is because the useApiCall
 * // handles the cancel when the component unmounts and when it repeats the call.
 */
const useApiCall = ({ apiCall, onSuccess, onError, startAsLoading = false }) => {
  const abortController = useRef(null);
  const [isLoading, setLoading] = useState(startAsLoading);

  const apiCallback = useCallback((...args) => {
    setLoading(true);
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();
    apiCall(abortController.current.signal, ...args)
      .then(result => {
        setLoading(false);
        onSuccess(result, ...args);
      }).catch(error => {
        if (API.isCancel(error)) {
          if (abortController.current) {
            setLoading(false);
          }
        } else {
          setLoading(false);
          onError(error);
        }
      }).finally(() => {
        abortController.current = null;
      });
  }, [apiCall, onSuccess, onError]);

  useEffect(() => {
    return () => {
      const prev = abortController.current;
      abortController.current = null;
      if (prev) {
        prev.abort();
      }
    };
  }, []);

  const cancel = useCallback(() => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
  }, []);
  return [apiCallback, isLoading, cancel];
};

export default useApiCall;
